import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Shows a running total (unit price x quantity) below the quantity stepper,
 * with a struck-through compare-at total and "Save X%" badge when the
 * selected variant has a discount. Updates live as the shopper changes
 * quantity, mirroring the pricing display at the top of the page.
 *
 * @typedef {Object} HbQuantityTotalPriceRefs
 * @property {HTMLElement} [total]
 * @property {HTMLElement} [compare]
 * @property {HTMLElement} [saveBadge]
 *
 * @extends {Component<HbQuantityTotalPriceRefs>}
 */
class HbQuantityTotalPrice extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #handleUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    this.#update();
  };

  #getCurrentQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    return quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
  }

  #update() {
    const unitPrice = Number(this.dataset.unitPrice || 0);
    const compareAtPrice = Number(this.dataset.compareAtPrice || 0);
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const quantity = this.#getCurrentQuantity();

    if (this.refs.total) {
      this.refs.total.textContent = formatMoney(unitPrice * quantity, format, currency);
    }

    const hasDiscount = compareAtPrice > unitPrice;

    if (this.refs.compare) {
      this.refs.compare.hidden = !hasDiscount;
      if (hasDiscount) {
        this.refs.compare.textContent = formatMoney(compareAtPrice * quantity, format, currency);
      }
    }

    if (this.refs.saveBadge) {
      this.refs.saveBadge.hidden = !hasDiscount;
      if (hasDiscount) {
        const discountPct = Math.round(((compareAtPrice - unitPrice) / compareAtPrice) * 100);
        this.refs.saveBadge.textContent = `Save ${discountPct}%`;
      }
    }
  }
}

if (!customElements.get('hb-quantity-total-price')) {
  customElements.define('hb-quantity-total-price', HbQuantityTotalPrice);
}
